import { isNotEmpty, useForm } from '@mantine/form'
import { useDispatch, useSelector } from 'react-redux'
import { getFacility } from 'app/state/ducks/facilities/selectors'
import { useEffect, useState } from 'react'
import { LoanParams } from 'app/models/loan-params'
import {
  SelectItem,
} from '@mantine/core'
import {
  rateTypeOptions,
  Option,
} from 'app/models/dropdown-options'
import {
  formatDateToUTC,
  stringToDate,
  toOptionalFxRate,
  validateOptionalFxRate,
} from 'app/utils/util-functions'
import {
  loadAllLoanConversions,
  saveLoanConversion,
} from 'app/state/ducks/loan-conversions/thunks'
import { getLoanConversions } from 'app/state/ducks/loan-conversions/selectors'
import { LoanConversionParams } from 'app/models/loan-conversion-params'
import { loadOneFacility } from 'app/state/ducks/facilities/thunks'
import { ContractPeriod, IroValues } from 'app/models/facility-params'
import axios from 'axios'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'
import { getIndexRateOptions } from 'app/state/ducks/index-rate-options/selectors'
import { loadIndexRateOptions } from 'app/state/ducks/index-rate-options/thunks'
import { getInterestRatess } from 'app/state/ducks/interest-rates/selectors'
import { loadInterestByIndex } from 'app/state/ducks/interest-rates/thunks'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import ConversionFormPresentation from './conversion-form-presentation'
import { calculateNonTermInterests } from '../loans/utilities/calculate-non-term-interest'
import { changeContractPeriod } from '../loans/utilities/change-contract-period'
import { calculateTermInterests } from '../loans/utilities/calculate-term-interest'
import { getPikConfig, arePikFieldsApplicable } from '../loans/utilities/pik-config-utils'
import { deriveAllInRate } from 'app/utils/rate-rounding'
import { CenteredLoader } from 'app/views/components/loader/loader'
import { usePrerequisiteReads } from 'app/hooks/usePrerequisiteReads'
import { listOf } from 'app/state/utils/load-result'

type Props = {
  close: any
  loan: LoanParams
}

export default function ConversionFormLogic({ close, loan }: Props) {
  const dispatch = useDispatch()

  const facility = useSelector(getFacility)
  const loanConversion = useSelector(getLoanConversions)
  const allIndexes = useSelector(getIndexRateOptions)
  const dailyRates = useSelector(getInterestRatess)
  const businessDate = useSelector(getBusinessDate)

  const pikConfig = facility ? getPikConfig(facility.pikOption) : getPikConfig()

  const [facilityindexOptions, setFacilityIndexOptions] = useState<Option[]>([])
  const [iro, setIro] = useState<IroValues | undefined>(undefined)
  const [contractPeriod, setContractPeriod] = useState<ContractPeriod>()
  const [disableEndDate, setDisableEndDate] = useState(false)
  const [facilityContractPeriodOptions, setFacilityContractPeriodOptions] =
    useState<SelectItem[]>([])

  // THIS form's read of the loan's conversions, kept apart from the shared
  // slice another form's later answer may replace; the slice is the fallback
  const [own, setOwn] = useState<LoanConversionParams[]>([])
  const conversion =
    own.find(lr => lr?.loanId?.id === loan?.id) ??
    loanConversion.find(lr => lr?.loanId?.id === loan?.id) ??
    undefined

  useEffect(() => {
    if (conversion) {
      const formConversion = {
        ...conversion.newLoan,
        status: conversion?.status,
        amount: Number(conversion?.newLoan.amount) ?? Number(loan.amount),
        interestBaseRate: Number(conversion?.newLoan.interestBaseRate ?? 0.0),
        indexOption: conversion?.newLoan.indexOption.id ?? '',
        interestBaseRateWithRounding:
          Number(conversion?.newLoan.interestBaseRateWithRounding ?? 0.0),
        endDate: conversion?.newLoan.endDate ? stringToDate(conversion.newLoan.endDate) : stringToDate(loan.endDate),
        startDate: conversion?.newLoan.startDate
          ? stringToDate(conversion.newLoan.startDate)
          : businessDate,
        margin: Number(conversion?.newLoan.margin ?? 0.0),
        allInRate: conversion?.newLoan.allInRateWithPikPremium
          ? Number(conversion.newLoan.allInRateWithPikPremium)
          : Number(conversion?.newLoan.allInRate ?? 0.0),
        casValue: Number(conversion?.newLoan.casValue ?? 0.0),
        fxRate: conversion?.newLoan.fxRate != null
          ? Number(conversion.newLoan.fxRate)
          : (loan.fxRate != null ? Number(loan.fxRate) : null),
        newLoanWithPik: conversion?.newLoan.pikPremium !== undefined || conversion?.newLoan.pikInterestPercent !== undefined || pikConfig.defaultNewLoanPikEnabled,
        // Carry over PIK Premium, but always default cash/PIK interest % to 0 (force user to re-enter since base rate changes)
        pikPremium: conversion?.newLoan.pikPremium ? Number(conversion.newLoan.pikPremium) : (loan?.pikPremium ? Number(loan.pikPremium) : 0),
        pikInterestPercent: conversion?.newLoan.pikInterestPercent ? Number(conversion.newLoan.pikInterestPercent) : 0,
        cashInterestPercent: conversion?.newLoan.cashInterestPercent ? Number(conversion.newLoan.cashInterestPercent) : 0,
        allInRateWithPikPremium: conversion?.newLoan.allInRateWithPikPremium
          ? Number(conversion.newLoan.allInRateWithPikPremium)
          : (conversion?.newLoan.allInRate ? Number(conversion.newLoan.allInRate) + (conversion?.newLoan.pikPremium ? Number(conversion.newLoan.pikPremium) : 0) : 0),
        hasError: false,
        ErrorMessage: '',
        hasSuccessfulEntitySave: false,
        customer_token: '',
        readyForApproval: false,
      }
      form.setValues(formConversion)
    } else {
      const cleanForm = {
        id: undefined,
        status: undefined,
        facilityId: loan.facilityId,
        amount: Number(loan.amount),
        currency: loan.currency,
        // Seeded from the converting loan; the ledger requires a rate whenever
        // the loan currency differs from the facility base currency.
        fxRate: loan.fxRate != null ? Number(loan.fxRate) : null,
        dayBasis: "",
        interestBaseRate: 0.0,
        indexOption: "",
        interestBaseRateWithRounding: 0.0,
        rounding: "",
        endDate: stringToDate(loan.endDate),
        startDate: businessDate,
        isSwingLine: loan.isSwingLine,
        isNonProRata: loan.isNonProRata,
        margin: 0.0,
        allInRate: 0.0,
        amountDue: null,
        accrualInterestMaturityDate: null,
        casValue: 0.0,
        contractPeriod: '',
        borrower: loan.borrower,
        borrowerPaymentInstructions: loan.borrowerPaymentInstructions,
        hasError: false,
        ErrorMessage: '',
        hasSuccessfulEntitySave: false,
        customer_token: '',
        nextInterestPaymentDate: null,
        nextPIKDate: null,
        newLoanWithPik: pikConfig.defaultNewLoanPikEnabled,
        pikPremium: 0,
        pikInterestPercent: 0,
        cashInterestPercent: 0,
        allInRateWithPikPremium: 0
      }
      form.setValues(cleanForm)
    }
  }, [conversion])


  useEffect(() => {
    if (!facility) {
      return
    }
    const indexRateOptions = facility.iroValues.find(
      iro => iro.indexOption.id == form.values.indexOption
    )
    setIro(indexRateOptions)
    if (!indexRateOptions) {
      return
    }
    if (form.values.rounding === '' || form.values.rounding === null) {
      form.setFieldValue('rounding', indexRateOptions.rounding)
    }
    if (
      form.values.margin === '' ||
      form.values.margin === 0 ||
      form.values.margin === null
    ) {
      form.setFieldValue('margin', Number(indexRateOptions.margin))
    }
    if (form.values.dayBasis === '' || form.values.dayBasis === null) {
      form.setFieldValue('dayBasis', indexRateOptions.dayBasis)
    }
    const selectedContractPeriod = indexRateOptions.contractPeriod.find(
      cp => cp.contractPeriod == form.values.contractPeriod
    )
    setContractPeriod(selectedContractPeriod)
  }, [facility, conversion])


  // The form reads its facility and the loan's existing conversion from Redux;
  // load them here so it works wherever it is opened from.
  // The form opens once they have settled: before that the existing draft is
  // unknown and a Save would create a second one. The index rate options are
  // reference data the header loads only when its own form opens.
  const ready = usePrerequisiteReads(
    () => [
      dispatch(loadOneFacility(loan.facilityId.id, 'Approved')),
      dispatch(loadAllLoanConversions(loan.id)),
      ...(allIndexes?.length ? [] : [dispatch(loadIndexRateOptions())]),
    ],
    () => {
      // a failed read is not "no draft": opening would let a Save create a
      // second one. Say so and close; the menu can be opened again.
      ErrorNotification({
        title: 'Could not open',
        message: `The loan's facility, its existing conversion or the index rate options could not be loaded. Please try again.`,
      })
      close()
    },
    { onLoaded: results => setOwn(listOf<LoanConversionParams>(results[1])) },
  )

  // set interest on edit
  useEffect(() => {
    if (!dailyRates) {
      return
    }
    if (allIndexes.find(index => index.id === form.values.indexOption)?.indexType !== 'TermIndex') {
      calculateNonTermInterests(form, conversion?.newLoan, dailyRates, allIndexes, iro, form.values?.startDate);
    }
  }, [dailyRates])

  useEffect(() => {
    if (!allIndexes || allIndexes.length == 0 || !facility) {
      return
    }
    const facilityIndexes = [...new Set(facility?.iroValues.map(iro => {
      return allIndexes.find(option => option.id === iro.indexOption.id)
    }))]


    setFacilityIndexOptions(
      facilityIndexes.map(indexOption => ({
        value: indexOption?.id ?? '',
        label: indexOption?.indexOption ?? '',
      })) ?? []
    )


    const selectedIndex = allIndexes.find(
      index => index.id == form.values.indexOption
    )
    if (selectedIndex) {
      dispatch(loadInterestByIndex(selectedIndex))
    }
  }, [allIndexes, facility])


  useEffect(() => {
    if (!loan) {
      return
    }
    const filteredIros = facility?.iroValues.find(
      iro =>
        iro.indexOption.id == form.values.indexOption &&
        form.values.currency === iro.currency
    )
    if (!filteredIros) {
      return
    }
    const contractPeriods = filteredIros.contractPeriod.map(
      iro => iro.contractPeriod
    )
    const filteredContractPeriodOptions = rateTypeOptions.filter(option =>
      contractPeriods.includes(option.value)
    )
    setFacilityContractPeriodOptions(filteredContractPeriodOptions)
  }, [facility, loan])

  const form: any = useForm({
    initialValues: {
      status: conversion?.status,
      id: conversion?.newLoan.id ?? undefined,
      facilityId: loan.facilityId,
      amount: Number(conversion?.newLoan.amount ?? loan.amount),
      currency:
        conversion?.newLoan.currency ?? loan.currency,
      // Seeded from the draft conversion, else the converting loan. Required by
      // the ledger whenever the loan currency differs from the facility's base.
      fxRate: conversion?.newLoan.fxRate != null
        ? Number(conversion.newLoan.fxRate)
        : (loan.fxRate != null ? Number(loan.fxRate) : null),
      dayBasis: conversion?.newLoan.dayBasis ?? "",
      interestBaseRate: Number(conversion?.newLoan.interestBaseRate ?? 0.0),
      indexOption: conversion?.newLoan.indexOption.id ?? "",
      interestBaseRateWithRounding:
        Number(conversion?.newLoan.interestBaseRateWithRounding ?? 0.0),
      rounding: conversion?.newLoan.rounding ?? "",
      endDate: conversion?.newLoan.endDate ? stringToDate(conversion.newLoan.endDate) : stringToDate(loan.endDate),
      startDate: conversion?.newLoan.startDate
        ? stringToDate(conversion.newLoan.startDate)
        : businessDate,
      isSwingLine: loan.isSwingLine,
      isNonProRata: loan.isNonProRata,
      margin: Number(conversion?.newLoan.margin ?? 0.0),
      allInRate: Number(conversion?.newLoan.allInRate ?? 0.0),
      amountDue: null,
      accrualInterestMaturityDate: null,
      casValue: Number(conversion?.newLoan.casValue ?? 0.0),
      contractPeriod: conversion?.newLoan.contractPeriod ?? '',
      borrower: loan.borrower,
      borrowerPaymentInstructions:
        loan.borrowerPaymentInstructions,
      hasError: false,
      ErrorMessage: '',
      hasSuccessfulEntitySave: false,
      customer_token: '',
      readyForApproval: false,
      nextInterestPaymentDate: conversion?.newLoan.nextInterestPaymentDate ?? null,
      nextPIKDate: conversion?.newLoan.nextPIKDate ?? null,
      newLoanWithPik: pikConfig.defaultNewLoanPikEnabled || false,
      pikPremium: Number(conversion?.newLoan.pikPremium ?? (loan?.pikPremium ?? 0)),
      pikInterestPercent: Number(conversion?.newLoan.pikInterestPercent ?? 0),
      cashInterestPercent: Number(conversion?.newLoan.cashInterestPercent ?? 0),
      allInRateWithPikPremium: Number(conversion?.newLoan.allInRateWithPikPremium ?? 0)
    },
    transformValues: values => {
      const indexOption = allIndexes.find(
        option => option.id === values.indexOption
      )
      return {
        id: values.id,
        status: values.status,
        loanId: { admin: loan.agencyAdmin, id: loan.id },
        newLoan: {
          ...values,
          fxRate: toOptionalFxRate(values.fxRate),
          startDate: formatDateToUTC(values.startDate),
          endDate: formatDateToUTC(values.endDate as Date),
          rounding: values.rounding != '' ? values.rounding : null,
          dayBasis: values.dayBasis != '' ? values.dayBasis : null,
          indexOption: {
            id: indexOption?.id ?? '',
            admin: indexOption?.public ?? '',
          },
          contractPeriod:
            values.contractPeriod != '' ? values.contractPeriod : null,
          allInRate: (values.allInRate != null && values.allInRate !== 0)
            ? Number(values.allInRate) - (Number(values.pikPremium) || 0)
            : null,
          allInRateWithPikPremium: (values.allInRate != null && values.allInRate !== 0)
            ? Number(values.allInRate)
            : null,
          interestBaseRate:
            values.interestBaseRate !== 0.0 ? values.interestBaseRate : null,
          interestBaseRateWithRounding:
            values.interestBaseRateWithRounding !== 0.0
              ? Number(values.interestBaseRateWithRounding)
              : null,
          pikPremium: arePikFieldsApplicable(facility?.pikOption, values.newLoanWithPik === true)
            ? (values.pikPremium != null ? Number(values.pikPremium) : null)
            : null,
          pikInterestPercent: arePikFieldsApplicable(facility?.pikOption, values.newLoanWithPik === true)
            ? Number(values.pikInterestPercent || 0)
            : null,
          cashInterestPercent: arePikFieldsApplicable(facility?.pikOption, values.newLoanWithPik === true)
            ? Number(values.cashInterestPercent || 0)
            : null,
        }
      }
    },
    validate: {
      amount: (value: number) => (value > 0 ? null : 'Invalid amount'),
      // Only enforced while the field is visible. The rate is optional — only an
      // entered value has to be positive.
      fxRate: (value: number | null | undefined, values: any) => {
        if (!facility?.baseCurrency || values.currency === facility.baseCurrency) {
          return null
        }
        return validateOptionalFxRate(value)
      },
      status: (value?: string) =>
        !value || value === 'Draft' ? null : 'Has already been submitted',
      endDate: (value: any, values) =>
        isNotEmpty(value)
          ? value > values.startDate
            ? null
            : 'Maturity has to be after Start Date'
          : 'Maturity is required'

      ,
      startDate: (value: any) => (!loan.startDate ||
        stringToDate(loan.startDate) <= value)
        ? null
        : 'Start Date is before loan start date'


    },
  })


  useEffect(() => {
    if (!form.values.indexOption) {
      return
    }
    const selectedIndex = allIndexes.find(index => index.id === form.values.indexOption)
    if (!selectedIndex) {
      return
    }
    setDisableEndDate(selectedIndex?.indexType !== 'TermIndex')
  }, [form.values.indexOption, allIndexes])

  const resetInterest = () => {
    form.setFieldValue('interestBaseRate', 0)
    form.setFieldValue('interestBaseRateWithRounding', 0)
    form.setFieldValue('allInRate', 0)
    form.clearFieldError('contractPeriod')
    form.clearFieldError('startDate')
  }

  const changeIndexOption = (event: string | null) => {
    if (!event) {
      return
    }
    const selectedIndex = allIndexes.find(index => index.id === event)
    if (selectedIndex?.indexType === 'TermIndex') {
      setDisableEndDate(false)
    } else {
      setDisableEndDate(true)
    }

    if (selectedIndex) {
      dispatch(loadInterestByIndex(selectedIndex))
    }
    form.setFieldValue('indexOption', event)
    form.setFieldValue('casValue', 0)
    form.setFieldValue('allInRate', 0)
    form.setFieldValue('contractPeriod', '')
    setContractPeriod(undefined)
    const filteredIros =
      facility?.iroValues.find(
        iro => iro.indexOption.id === event && form.values.currency === iro.currency
      ) ?? undefined
    if (!filteredIros) {
      form.setFieldValue('rounding', '')
      form.setFieldValue('margin', 0)
      form.setFieldValue('dayBasis', '')
      resetInterest()
      return
    }
    form.setFieldValue('rounding', filteredIros.rounding)
    form.setFieldValue('margin', Number(filteredIros.margin))
    form.setFieldValue('dayBasis', filteredIros.dayBasis)
    setIro(filteredIros)

    const contractPeriods = filteredIros.contractPeriod.map(
      cp => cp.contractPeriod
    )
    const filteredContractPeriodOptions = rateTypeOptions.filter(option =>
      contractPeriods.includes(option.value)
    )
    if (selectedIndex?.indexType !== 'TermIndex') {
      form.setFieldValue('contractPeriod', null)
      form.setFieldValue('endDate', stringToDate((facility?.maturity ?? '')))
    } else {
      resetInterest()
      setFacilityContractPeriodOptions(filteredContractPeriodOptions)
    }
  }

  const changeContractPeriodInForm = (event: string | null) => {
    if (!event) {
      return
    }
    changeContractPeriod(form, iro, conversion?.newLoan, dailyRates, event, setContractPeriod, form.values?.startDate);
  }

  const createDraftConversion = async () => {
    try {

      const loanConversionVals: LoanConversionParams = form.getTransformedValues()
      const response: any = await dispatch(
        saveLoanConversion(loanConversionVals, conversion)
      )

      if (response.success) {
        SuccessNotification({
          title: 'Successfully Saved Draft Loan Conversion',
          message: 'You created or edited a Loan Conversion',
        })
      } else {
        ErrorNotification({
          title: 'Failed to Create Conversion',
          message: response.payload ?? 'Please check inputs',
        })
      }
      return response
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status == 400) {
        const data: any = error.response.data
        ErrorNotification({
          title: 'Failed to Create Conversion',
          message: data?.error ?? 'Please check inputs',
        })
        return null
      } else {
        ErrorNotification({
          title: 'Failed to Create Conversion',
          message: error,
        })
        return null
      }
    } finally {
      form.reset()
    }
  }

  function changeStartDate(value: any) {
    if (allIndexes.find(index => index.id === form.values.indexOption)?.indexType !== 'TermIndex') {
      calculateNonTermInterests(form, conversion?.newLoan, dailyRates, allIndexes, iro, value);
    } else {
      calculateTermInterests(form, conversion?.newLoan, dailyRates, iro, contractPeriod, value);
    }
  }

  function onInterestBaseRateChange(value: number | ''): void {
    form.setFieldValue('interestBaseRate', value)
    const { roundedBaseRate, allInRate } = deriveAllInRate({
      interestBaseRate: value,
      rounding: form.values.rounding,
      margin: form.values.margin,
      casValue: form.values.casValue,
    })
    form.setFieldValue('interestBaseRateWithRounding', roundedBaseRate)
    form.setFieldValue('allInRate', allInRate)
  }

  // the saved record decides create vs edit: nothing is editable until it is known
  if (!ready) return <CenteredLoader />

  return (
    <ConversionFormPresentation close={close} form={form} facility={facility} loan={loan} conversion={conversion} facilityindexOptions={facilityindexOptions} disableEndDate={disableEndDate} facilityContractPeriodOptions={facilityContractPeriodOptions} changeIndexOption={changeIndexOption} changeStartDate={changeStartDate} changeContractPeriod={changeContractPeriodInForm} onInterestBaseRateChange={onInterestBaseRateChange} createDraftConversion={createDraftConversion} />
  )
}
